import { RevenueMarketEditor } from '@/components/travel/revenue-market';
import { DirectionEditor } from '@/components/travel/direction';
import React, { useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { pdf } from '@react-pdf/renderer';
import {
  ArrowLeft,
  Receipt,
  Loader2,
  User,
  DollarSign,
  CreditCard,
  Send,
  MoreHorizontal,
  Plus,
  Trash2,
  Download,
  FileText,
  CheckCircle,
  XCircle,
  AlertCircle,
  Printer,
  Pencil,
  Check,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { trpc } from '@/lib/trpc';
import { formatCurrency } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth-store';
import { InvoicePDF } from '@/components/travel/invoices/invoice-pdf';
import { InvoicePaymentModal } from '@/components/travel/invoices/invoice-payment-modal';
import { InvoiceItemModal } from '@/components/travel/invoices/invoice-item-modal';
import { CurrencyConversionModal } from '@/components/invoices/currency-conversion-modal';
import { CancellationReasonDialog } from '@/components/ui/cancellation-reason-dialog';

type InvoiceStatus = 'DRAFT' | 'SENT' | 'VIEWED' | 'PAID' | 'PARTIALLY_PAID' | 'OVERDUE' | 'CANCELLED' | 'REFUNDED';

const statusColors: Record<string, string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  SENT: 'bg-blue-100 text-blue-700',
  VIEWED: 'bg-purple-100 text-purple-700',
  PAID: 'bg-green-100 text-green-700',
  PARTIALLY_PAID: 'bg-orange-100 text-orange-700',
  OVERDUE: 'bg-red-100 text-red-700',
  CANCELLED: 'bg-gray-100 text-gray-500',
  REFUNDED: 'bg-pink-100 text-pink-700',
};

const statusLabelKeys: Record<string, string> = {
  DRAFT: 'status.draft',
  SENT: 'status.sent',
  VIEWED: 'status.viewed',
  PAID: 'status.paid',
  PARTIALLY_PAID: 'status.partiallyPaid',
  OVERDUE: 'status.overdue',
  CANCELLED: 'status.cancelled',
  REFUNDED: 'status.refunded',
};

const typeLabelKeys: Record<string, string> = {
  PROFORMA: 'type.proformaInvoice',
  INVOICE: 'type.taxInvoice',
  CREDIT_NOTE: 'type.creditNote',
};

const paymentMethodKeys: Record<string, string> = {
  CASH: 'payments.methods.cash',
  CREDIT_CARD: 'payments.methods.creditCard',
  DEBIT_CARD: 'payments.methods.debitCard',
  BANK_TRANSFER: 'payments.methods.bankTransfer',
  PAYPAL: 'payments.methods.paypal',
  CHEQUE: 'payments.methods.cheque',
  OTHER: 'payments.methods.other',
};

function translateItemDescription(description: string, tBookings: (key: string, options?: Record<string, unknown>) => string): string {
  // Description format: "service_name" or "service_name - extra details"
  const dashIndex = description.indexOf(' - ');
  const serviceName = dashIndex > -1 ? description.substring(0, dashIndex) : description;
  const extra = dashIndex > -1 ? description.substring(dashIndex) : '';

  const key = `items.serviceNames.${serviceName}`;
  const translated = tBookings(key, { defaultValue: '' });
  if (translated && translated !== key) {
    return translated + extra;
  }
  return description;
}

export function InvoiceDetailPage() {
  const { t, i18n } = useTranslation('invoices');
  const { t: tBookings } = useTranslation('bookings');
  const dateLocale = i18n.language === 'tr' ? 'tr-TR' : 'en-US';
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isItemModalOpen, setIsItemModalOpen] = useState(false);
  const [conversionModalOpen, setConversionModalOpen] = useState(false);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const titleInputRef = useRef<HTMLInputElement>(null);
  const [isEditingBillTo, setIsEditingBillTo] = useState(false);
  const [editBillToName, setEditBillToName] = useState('');
  const billToInputRef = useRef<HTMLInputElement>(null);
  const { user } = useAuthStore();

  const { data: invoice, isLoading, error } = trpc.invoice.byId.useQuery(id!, {
    enabled: !!id,
  });

  const utils = trpc.useUtils();

  const updateStatusMutation = trpc.invoice.updateStatus.useMutation({
    onSuccess: () => {
      utils.invoice.byId.invalidate(id);
      utils.invoice.list.invalidate();
    },
  });

  const updateInvoiceMutation = trpc.invoice.update.useMutation({
    onSuccess: () => {
      utils.invoice.byId.invalidate(id);
      setIsEditingTitle(false);
    },
  });

  const handleStartEditTitle = () => {
    setEditTitle(invoice?.invoiceTitle || '');
    setIsEditingTitle(true);
    setTimeout(() => titleInputRef.current?.focus(), 0);
  };

  const handleSaveTitle = () => {
    if (!id) return;
    updateInvoiceMutation.mutate({
      id,
      invoiceTitle: editTitle || undefined,
      userId: user?.id,
    });
  };

  const handleCancelEditTitle = () => {
    setIsEditingTitle(false);
  };

  const handleStartEditBillTo = () => {
    setEditBillToName(invoice?.billToName || '');
    setIsEditingBillTo(true);
    setTimeout(() => billToInputRef.current?.focus(), 0);
  };

  const handleSaveBillTo = () => {
    if (!id) return;
    updateInvoiceMutation.mutate({
      id,
      billToName: editBillToName || undefined,
      userId: user?.id,
    });
    setIsEditingBillTo(false);
  };

  const handleSameAsCustomer = () => {
    if (!id || !invoice) return;
    const customerName = `${invoice.customer.firstName} ${invoice.customer.lastName}`;
    updateInvoiceMutation.mutate({
      id,
      billToName: customerName,
      userId: user?.id,
    });
    setIsEditingBillTo(false);
  };

  const removeItemMutation = trpc.invoice.removeItem.useMutation({
    onSuccess: () => {
      utils.invoice.byId.invalidate(id);
    },
  });

  const invoiceCurrencies = React.useMemo(() => {
    if (!invoice?.items) return [];
    const currencies = new Set(invoice.items.map((item: any) => item.currency || invoice.currency));
    return Array.from(currencies);
  }, [invoice]);

  const hasMultipleCurrencies = invoiceCurrencies.length > 1;

  const handleStatusChange = (newStatus: InvoiceStatus, cancellationReason?: string) => {
    if (!id) return;
    updateStatusMutation.mutate({ id, status: newStatus, userId: user?.id, cancellationReason });
  };

  const handleCancelInvoice = (reason: string) => {
    handleStatusChange('CANCELLED', reason);
    setIsCancelDialogOpen(false);
  };

  const handleRemoveItem = (itemId: string) => {
    if (confirm(t('lineItems.confirmRemove'))) {
      removeItemMutation.mutate(itemId);
    }
  };

  const generateAndDownloadPdf = async () => {
    if (!invoice) return;

    // Type assertion for booking with extended fields
    const bookingData = invoice.booking as {
      bookingReference: string;
      travelStartDate?: Date;
      travelEndDate?: Date;
      totalAdults?: number;
      totalChildren?: number;
      totalInfants?: number;
      package?: { id: string; name: string; code: string } | null;
    } | null;

    const pdfData = {
      invoiceNumber: invoice.invoiceNumber,
      invoiceType: invoice.invoiceType,
      invoiceTitle: invoice.invoiceTitle,
      status: invoice.status,
      issueDate: invoice.issueDate.toString(),
      dueDate: invoice.dueDate.toString(),
      paidAt: invoice.paidAt?.toString() || null,
      subtotal: invoice.subtotal,
      discountAmount: invoice.discountAmount,
      taxAmount: invoice.taxAmount,
      totalAmount: invoice.totalAmount,
      amountPaid: invoice.amountPaid,
      balanceDue: invoice.balanceDue,
      currency: invoice.currency,
      notes: invoice.notes,
      terms: invoice.terms,
      // Bill-to override fields
      billToName: invoice.billToName,
      billToCompany: invoice.billToCompany,
      billToTaxId: invoice.billToTaxId,
      billToAddress: invoice.billToAddress,
      billToEmail: invoice.billToEmail,
      billToPhone: invoice.billToPhone,
      customer: {
        firstName: invoice.customer.firstName,
        lastName: invoice.customer.lastName,
        email: invoice.customer.email ?? '',
        phone: invoice.customer.phone ?? '',
        address: invoice.customer.addressLine1 ?? '',
        city: invoice.customer.city ?? '',
        country: invoice.customer.country ?? '',
      },
      createdBy: {
        firstName: invoice.createdBy.firstName,
        lastName: invoice.createdBy.lastName,
        email: invoice.createdBy.email,
      },
      items: invoice.items.map((item) => ({
        id: item.id,
        description: item.description,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        taxRate: item.taxRate,
        taxAmount: item.taxAmount,
        totalAmount: item.totalAmount,
      })),
      booking: bookingData
        ? {
            bookingReference: bookingData.bookingReference,
            travelStartDate: bookingData.travelStartDate?.toString(),
            travelEndDate: bookingData.travelEndDate?.toString(),
            totalAdults: bookingData.totalAdults,
            totalChildren: bookingData.totalChildren,
            totalInfants: bookingData.totalInfants,
            package: bookingData.package ?? null,
          }
        : null,
    };

    const blob = await pdf(<InvoicePDF invoice={pdfData} logoUrl="/polatkan-logo.png" />).toBlob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${invoice.invoiceNumber}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDownloadPdf = () => {
    if (hasMultipleCurrencies) {
      setConversionModalOpen(true);
    } else {
      // Direct download for single currency invoice
      generateAndDownloadPdf();
    }
  };

  const handleExportWithConversion = (targetCurrency: string, rates: Record<string, number>) => {
    // This will be used to generate the PDF with converted amounts
    console.log('Export with conversion:', targetCurrency, rates);
    // TODO: Implement actual PDF generation with converted values
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2 text-muted-foreground">{t('detail.loadingInvoice')}</span>
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => navigate('/travel/invoices')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('detail.backToInvoices')}
        </Button>
        <div className="border border-red-200 bg-red-50 p-4 text-red-700">
          <p className="font-medium">{t('detail.errorLoading')}</p>
          <p className="text-sm">{error?.message || t('detail.invoiceNotFound')}</p>
        </div>
      </div>
    );
  }

  const paymentProgress = invoice.totalAmount > 0
    ? Math.round((invoice.amountPaid / invoice.totalAmount) * 100)
    : 0;

  const isOverdue = new Date(invoice.dueDate) < new Date() &&
    !['PAID', 'CANCELLED', 'REFUNDED'].includes(invoice.status);

  return (
    <div className="space-y-6">
      <DirectionEditor kind="invoice" id={invoice.id} value={invoice.direction} booking={invoice.booking} />
      <RevenueMarketEditor kind="invoice" id={invoice.id} value={invoice.revenueMarket} booking={invoice.booking} />
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/travel/invoices')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('detail.back')}
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold">{invoice.invoiceNumber}</h1>
              <span
                className={`px-2 py-0.5 text-xs font-medium ${
                  statusColors[invoice.status] || 'bg-gray-100 text-gray-700'
                }`}
              >
                {t(statusLabelKeys[invoice.status] || 'status.unknown')}
              </span>
              {isOverdue && invoice.status !== 'OVERDUE' && (
                <span className="bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
                  {t('status.overdue')}
                </span>
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              {t(typeLabelKeys[invoice.invoiceType] || 'type.unknown')}
            </p>
            {/* Editable Invoice Title */}
            {isEditingTitle ? (
              <div className="flex items-center gap-2 mt-1">
                <Input
                  ref={titleInputRef}
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  placeholder={t('detail.titlePlaceholder')}
                  className="h-7 text-sm w-64"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveTitle();
                    if (e.key === 'Escape') handleCancelEditTitle();
                  }}
                />
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleSaveTitle} disabled={updateInvoiceMutation.isPending}>
                  {updateInvoiceMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5 text-green-600" />}
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleCancelEditTitle}>
                  <X className="h-3.5 w-3.5 text-red-500" />
                </Button>
              </div>
            ) : (
              <button
                onClick={handleStartEditTitle}
                className="flex items-center gap-1.5 mt-1 text-sm text-muted-foreground hover:text-foreground transition-colors group"
              >
                {invoice.invoiceTitle ? (
                  <span>{invoice.invoiceTitle}</span>
                ) : (
                  <span className="italic">{t('detail.addTitle')}</span>
                )}
                <Pencil className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleDownloadPdf}>
            <Download className="mr-2 h-4 w-4" />
            {t('actions.downloadPdf')}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {invoice.status === 'DRAFT' && (
                <DropdownMenuItem onClick={() => handleStatusChange('SENT')}>
                  <Send className="mr-2 h-4 w-4" />
                  {t('actions.markAsSent')}
                </DropdownMenuItem>
              )}
              {invoice.status === 'SENT' && (
                <DropdownMenuItem onClick={() => handleStatusChange('VIEWED')}>
                  <FileText className="mr-2 h-4 w-4" />
                  {t('actions.markAsViewed')}
                </DropdownMenuItem>
              )}
              {['SENT', 'VIEWED', 'PARTIALLY_PAID', 'OVERDUE'].includes(invoice.status) && (
                <DropdownMenuItem onClick={() => setIsPaymentModalOpen(true)}>
                  <CreditCard className="mr-2 h-4 w-4" />
                  {t('actions.recordPayment')}
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              {invoice.status !== 'CANCELLED' && invoice.status !== 'REFUNDED' && (
                <DropdownMenuItem
                  onClick={() => setIsCancelDialogOpen(true)}
                  className="text-red-600"
                >
                  <XCircle className="mr-2 h-4 w-4" />
                  {t('actions.cancelInvoice')}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main Content */}
        <div className="space-y-6 lg:col-span-2">
          {/* Invoice Summary Card */}
          <div className="border border-border bg-card p-6">
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <p className="text-sm text-muted-foreground">{t('detail.issueDate')}</p>
                <p className="mt-1 font-medium">
                  {new Date(invoice.issueDate).toLocaleDateString(dateLocale)}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('detail.dueDate')}</p>
                <p className={`mt-1 font-medium ${isOverdue ? 'text-red-600' : ''}`}>
                  {new Date(invoice.dueDate).toLocaleDateString(dateLocale)}
                  {isOverdue && <AlertCircle className="ml-1 inline h-4 w-4" />}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('detail.totalAmount')}</p>
                <p className="mt-1 text-lg font-bold">
                  {formatCurrency(invoice.totalAmount, invoice.currency)}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('detail.balanceDue')}</p>
                <p className={`mt-1 text-lg font-bold ${invoice.balanceDue <= 0 ? 'text-green-600' : 'text-orange-600'}`}>
                  {formatCurrency(invoice.balanceDue, invoice.currency)}
                </p>
              </div>
            </div>

            {/* Payment Progress */}
            <div className="mt-6">
              <div className="mb-2 flex justify-between text-sm">
                <span className="text-muted-foreground">{t('detail.paymentProgress')}</span>
                <span className="font-medium">{paymentProgress}%</span>
              </div>
              <div className="h-2 w-full overflow-hidden bg-gray-200">
                <div
                  className={`h-full transition-all ${paymentProgress >= 100 ? 'bg-green-500' : 'bg-blue-500'}`}
                  style={{ width: `${Math.min(100, paymentProgress)}%` }}
                />
              </div>
              <div className="mt-2 flex justify-between text-xs text-muted-foreground">
                <span>{t('amounts.paid')}: {formatCurrency(invoice.amountPaid, invoice.currency)}</span>
                <span>{t('detail.remaining')}: {formatCurrency(invoice.balanceDue, invoice.currency)}</span>
              </div>
            </div>
          </div>

          {/* Line Items */}
          <div className="border border-border bg-card">
            <div className="flex items-center justify-between border-b px-6 py-4">
              <h2 className="text-lg font-semibold">{t('lineItems.title')}</h2>
              {invoice.status === 'DRAFT' && (
                <Button size="sm" onClick={() => setIsItemModalOpen(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  {t('actions.addItem')}
                </Button>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-sm font-medium">{t('lineItems.description')}</th>
                    <th className="px-6 py-3 text-right text-sm font-medium">{t('lineItems.qty')}</th>
                    <th className="px-6 py-3 text-right text-sm font-medium">{t('lineItems.unitPrice')}</th>
                    <th className="px-6 py-3 text-right text-sm font-medium">{t('lineItems.tax')}</th>
                    <th className="px-6 py-3 text-right text-sm font-medium">{t('lineItems.total')}</th>
                    {invoice.status === 'DRAFT' && (
                      <th className="px-6 py-3 text-right text-sm font-medium">{t('table.actions')}</th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {invoice.items.map((item) => (
                    <tr key={item.id} className="hover:bg-muted/30">
                      <td className="px-6 py-4">
                        <p className="font-medium">{translateItemDescription(item.description, tBookings)}</p>
                      </td>
                      <td className="px-6 py-4 text-right">{item.quantity}</td>
                      <td className="px-6 py-4 text-right">
                        {formatCurrency(item.unitPrice, invoice.currency)}
                      </td>
                      <td className="px-6 py-4 text-right">
                        {item.taxRate > 0 ? (
                          <span>
                            {item.taxRate}%
                            <span className="text-xs text-muted-foreground ml-1">
                              ({formatCurrency(item.taxAmount, invoice.currency)})
                            </span>
                          </span>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="px-6 py-4 text-right font-medium">
                        {formatCurrency(item.totalAmount, invoice.currency)}
                      </td>
                      {invoice.status === 'DRAFT' && (
                        <td className="px-6 py-4 text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleRemoveItem(item.id)}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Totals */}
            <div className="border-t bg-muted/30 px-6 py-4">
              <div className="flex flex-col items-end space-y-2">
                <div className="flex w-64 justify-between">
                  <span className="text-muted-foreground">{t('lineItems.subtotal')}</span>
                  <span>{formatCurrency(invoice.subtotal, invoice.currency)}</span>
                </div>
                {invoice.discountAmount > 0 && (
                  <div className="flex w-64 justify-between text-green-600">
                    <span>{t('lineItems.discount')}</span>
                    <span>-{formatCurrency(invoice.discountAmount, invoice.currency)}</span>
                  </div>
                )}
                {invoice.taxAmount > 0 && (
                  <div className="flex w-64 justify-between">
                    <span className="text-muted-foreground">{t('lineItems.tax')}</span>
                    <span>{formatCurrency(invoice.taxAmount, invoice.currency)}</span>
                  </div>
                )}
                <div className="flex w-64 justify-between border-t pt-2 text-lg font-bold">
                  <span>{t('lineItems.total')}</span>
                  <span>{formatCurrency(invoice.totalAmount, invoice.currency)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Payments */}
          <div className="border border-border bg-card">
            <div className="flex items-center justify-between border-b px-6 py-4">
              <h2 className="text-lg font-semibold">{t('payments.title')}</h2>
              {['SENT', 'VIEWED', 'PARTIALLY_PAID', 'OVERDUE'].includes(invoice.status) && (
                <Button size="sm" onClick={() => setIsPaymentModalOpen(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  {t('actions.recordPayment')}
                </Button>
              )}
            </div>
            {invoice.payments.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <CreditCard className="h-8 w-8 text-muted-foreground/50" />
                <p className="mt-2 text-sm text-muted-foreground">{t('payments.noPayments')}</p>
              </div>
            ) : (
              <div className="divide-y">
                {invoice.payments.map((payment) => (
                  <div
                    key={payment.id}
                    className="flex items-center justify-between px-6 py-4 hover:bg-muted/30"
                  >
                    <div className="flex items-center gap-4">
                      <div className="flex h-10 w-10 items-center justify-center bg-green-100">
                        <DollarSign className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <p className="font-medium">
                          {formatCurrency(payment.amount, payment.currency)}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {t(paymentMethodKeys[payment.paymentMethod] || 'payments.methods.other')}
                          {payment.referenceNumber && ` • ${payment.referenceNumber}`}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm">
                        {new Date(payment.paymentDate).toLocaleDateString(dateLocale)}
                      </p>
                      <span className="bg-green-100 px-2 py-0.5 text-xs text-green-700">
                        {payment.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Notes & Terms */}
          {(invoice.notes || invoice.terms) && (
            <div className="grid gap-6 sm:grid-cols-2">
              {invoice.notes && (
                <div className="border border-border bg-card p-6">
                  <h3 className="mb-3 font-semibold">{t('notes.title')}</h3>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                    {invoice.notes}
                  </p>
                </div>
              )}
              {invoice.terms && (
                <div className="border border-border bg-card p-6">
                  <h3 className="mb-3 font-semibold">{t('notes.termsTitle')}</h3>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                    {invoice.terms}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Bill To Card */}
          <div className="border border-border bg-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">{t('sidebar.billTo')}</h3>
              {invoice.billToName !== `${invoice.customer.firstName} ${invoice.customer.lastName}` && (
                <button
                  onClick={handleSameAsCustomer}
                  className="text-xs text-primary hover:underline"
                  disabled={updateInvoiceMutation.isPending}
                >
                  {t('sidebar.sameAsCustomer')}
                </button>
              )}
            </div>

            {/* Editable Bill To Name */}
            {isEditingBillTo ? (
              <div className="flex items-center gap-2 mb-3">
                <Input
                  ref={billToInputRef}
                  value={editBillToName}
                  onChange={(e) => setEditBillToName(e.target.value)}
                  placeholder={t('sidebar.billToNamePlaceholder')}
                  className="h-8 text-sm"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveBillTo();
                    if (e.key === 'Escape') setIsEditingBillTo(false);
                  }}
                />
                <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={handleSaveBillTo}>
                  <Check className="h-3.5 w-3.5 text-green-600" />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => setIsEditingBillTo(false)}>
                  <X className="h-3.5 w-3.5 text-red-500" />
                </Button>
              </div>
            ) : (
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 items-center justify-center bg-primary/10 shrink-0">
                  <User className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1">
                  <button
                    onClick={handleStartEditBillTo}
                    className="font-medium text-left group flex items-center gap-1.5 hover:text-primary transition-colors"
                  >
                    {invoice.billToName || `${invoice.customer.firstName} ${invoice.customer.lastName}`}
                    <Pencil className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground" />
                  </button>
                  <p className="text-sm text-muted-foreground">{invoice.billToEmail || invoice.customer.email}</p>
                  {(invoice.billToPhone || invoice.customer.phone) && (
                    <p className="text-sm text-muted-foreground">{invoice.billToPhone || invoice.customer.phone}</p>
                  )}
                  {(invoice.billToAddress || invoice.customer.addressLine1) && (
                    <p className="mt-2 text-sm text-muted-foreground">
                      {invoice.billToAddress || (
                        <>
                          {invoice.customer.addressLine1}
                          {invoice.customer.city && `, ${invoice.customer.city}`}
                          {invoice.customer.country && `, ${invoice.customer.country}`}
                        </>
                      )}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Link to customer profile */}
            <Link
              to={`/travel/cari/accounts/customer/${invoice.customer.id}`}
              className="mt-3 text-xs text-muted-foreground hover:text-primary flex items-center gap-1"
            >
              {t('sidebar.viewCustomer')} →
            </Link>
          </div>

          {/* Booking Reference */}
          {invoice.booking && (
            <div className="border border-border bg-card p-6">
              <h3 className="mb-4 font-semibold">{t('sidebar.linkedBooking')}</h3>
              <Link
                to={`/travel/bookings/${invoice.booking.id}`}
                className="flex items-center gap-3 border border-border p-3 hover:bg-muted/50"
              >
                <div className="flex h-10 w-10 items-center justify-center bg-blue-100">
                  <Receipt className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium">{invoice.booking.bookingReference}</p>
                  {invoice.booking.package && (
                    <p className="text-sm text-muted-foreground">
                      {invoice.booking.package.name}
                    </p>
                  )}
                </div>
              </Link>
            </div>
          )}

          {/* Created By */}
          <div className="border border-border bg-card p-6">
            <h3 className="mb-4 font-semibold">{t('sidebar.createdBy')}</h3>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center bg-gray-100">
                <User className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <p className="font-medium">
                  {invoice.createdBy.firstName} {invoice.createdBy.lastName}
                </p>
                <p className="text-sm text-muted-foreground">{invoice.createdBy.email}</p>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="border border-border bg-card p-6">
            <h3 className="mb-4 font-semibold">{t('sidebar.quickActions')}</h3>
            <div className="space-y-2">
              <Button variant="outline" className="w-full justify-start" onClick={handleDownloadPdf}>
                <Download className="mr-2 h-4 w-4" />
                {t('actions.downloadPdf')}
              </Button>
              <Button variant="outline" className="w-full justify-start" onClick={() => window.print()}>
                <Printer className="mr-2 h-4 w-4" />
                {t('actions.printInvoice')}
              </Button>
              {invoice.status === 'DRAFT' && (
                <Button className="w-full justify-start" onClick={() => handleStatusChange('SENT')}>
                  <Send className="mr-2 h-4 w-4" />
                  {t('actions.sendInvoice')}
                </Button>
              )}
              {['SENT', 'VIEWED', 'PARTIALLY_PAID', 'OVERDUE'].includes(invoice.status) && (
                <Button className="w-full justify-start" onClick={() => setIsPaymentModalOpen(true)}>
                  <CreditCard className="mr-2 h-4 w-4" />
                  {t('actions.recordPayment')}
                </Button>
              )}
            </div>
          </div>

          {/* Timeline */}
          <div className="border border-border bg-card p-6">
            <h3 className="mb-4 font-semibold">{t('sidebar.timeline')}</h3>
            <div className="space-y-4">
              <div className="flex gap-3">
                <div className="flex h-6 w-6 items-center justify-center bg-green-100">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-medium">{t('timeline.created')}</p>
                  <p className="text-xs text-muted-foreground">
                    {new Date(invoice.createdAt).toLocaleString(dateLocale)}
                  </p>
                </div>
              </div>
              {invoice.sentAt && (
                <div className="flex gap-3">
                  <div className="flex h-6 w-6 items-center justify-center bg-blue-100">
                    <Send className="h-4 w-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">{t('timeline.sent')}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(invoice.sentAt).toLocaleString(dateLocale)}
                    </p>
                  </div>
                </div>
              )}
              {invoice.paidAt && (
                <div className="flex gap-3">
                  <div className="flex h-6 w-6 items-center justify-center bg-green-100">
                    <DollarSign className="h-4 w-4 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">{t('timeline.paidInFull')}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(invoice.paidAt).toLocaleString(dateLocale)}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {isPaymentModalOpen && (
        <InvoicePaymentModal
          open={isPaymentModalOpen}
          onOpenChange={setIsPaymentModalOpen}
          invoiceId={invoice.id}
          currency={invoice.currency}
          balanceDue={invoice.balanceDue}
          onSuccess={() => {
            utils.invoice.byId.invalidate(id);
            utils.invoice.list.invalidate();
          }}
        />
      )}

      {isItemModalOpen && (
        <InvoiceItemModal
          open={isItemModalOpen}
          onOpenChange={setIsItemModalOpen}
          invoiceId={invoice.id}
          currency={invoice.currency}
          onSuccess={() => {
            utils.invoice.byId.invalidate(id);
          }}
        />
      )}

      <CancellationReasonDialog
        open={isCancelDialogOpen}
        onOpenChange={setIsCancelDialogOpen}
        title={t('actions.cancelInvoice')}
        description={t('actions.cancelInvoiceDescription')}
        reasonLabel={t('actions.cancellationReason')}
        reasonPlaceholder={t('actions.cancellationReasonPlaceholder')}
        confirmLabel={t('actions.confirmCancel')}
        cancelLabel={t('actions.keepInvoice')}
        onConfirm={handleCancelInvoice}
        isLoading={updateStatusMutation.isPending}
      />

      {invoice && (
        <CurrencyConversionModal
          open={conversionModalOpen}
          onOpenChange={setConversionModalOpen}
          invoiceId={invoice.id}
          invoiceDate={new Date(invoice.createdAt)}
          currencies={invoiceCurrencies}
          onExport={handleExportWithConversion}
        />
      )}
    </div>
  );
}
